import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function CustodyConfirm() {
  const { token } = useParams();
  const [asset, setAsset] = useState(undefined);
  const [empNo, setEmpNo] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/itam/custody/${token}`).then((r) => setAsset(r.data)).catch(() => setAsset(null));
  }, [token]);

  const confirm = async () => {
    setErr(""); setLoading(true);
    try {
      await axios.post(`${BACKEND}/api/itam/custody/${token}/confirm`, { employee_no: empNo });
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not confirm custody");
    } finally { setLoading(false); }
  };

  if (asset === undefined) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (asset === null) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 text-slate-300" data-testid="custody-page">
      <div className="text-center"><Icons.SearchX className="h-10 w-10 mx-auto mb-3 text-slate-500" /><div className="font-semibold">Custody record not found</div></div>
    </div>
  );

  const already = asset.is_active || asset.status !== "assigned";

  return (
    <div className="min-h-screen bg-slate-900 py-10 px-4" data-testid="custody-page">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-2.5 mb-5 text-white">
          <Icons.ShieldCheck className="h-6 w-6 text-emerald-400" />
          <div className="font-semibold text-lg">Asset Custody Confirmation</div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-xl">
          <div className="text-xs font-mono text-slate-400">{asset.asset_tag}</div>
          <div className="text-xl font-semibold text-slate-900 mt-1">{asset.name}</div>
          <div className="text-sm text-slate-500">{asset.asset_type}{asset.serial_number ? ` · SN ${asset.serial_number}` : ""}</div>
          <div className="mt-2 text-sm text-slate-600">Assigned holder: <b>{asset.assigned_to_name || "—"}</b></div>

          {done || (already && asset.is_active) ? (
            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center text-emerald-800" data-testid="custody-confirmed">
              <Icons.CheckCircle2 className="h-10 w-10 mx-auto mb-2" />
              <div className="font-semibold">Custody confirmed</div>
              <div className="text-sm">Thank you — this device is now registered in your name.</div>
            </div>
          ) : already ? (
            <div className="mt-6 text-sm text-slate-500">This custody link is no longer active.</div>
          ) : (
            <div className="mt-6 space-y-3">
              <div className="text-sm text-slate-600">Enter your employee number to confirm you are holding this device.</div>
              <input value={empNo} onChange={(e) => setEmpNo(e.target.value)} placeholder="Employee No. (e.g. EMP-0001)"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" data-testid="custody-empno" />
              {err && <div className="text-sm text-rose-600" data-testid="custody-error">{err}</div>}
              <button onClick={confirm} disabled={!empNo || loading}
                className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-lg py-2.5 text-sm font-medium" data-testid="custody-confirm-btn">
                {loading ? "Confirming…" : "I confirm I have this device"}
              </button>
            </div>
          )}
        </div>
        <div className="text-center text-xs text-slate-500 mt-4">Premium Infinite Ventures Inc. · UEMP Asset Management</div>
      </div>
    </div>
  );
}
